import React from 'react';
import PropTypes from 'prop-types';

/**
 * ArticleActions - Componente molecular para os botões de ação do artigo
 * 
 * Este componente segue o princípio do Atomic Design como uma "molécula".
 */
const ArticleActions = ({
  actions = [],
  variant = 'default',
  className = '',
  onDelete,
  ...props
}) => {
  const styles = {
    container: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      ...(variant === 'compact' && { gap: '4px' }),
      ...(variant === 'list' && { justifyContent: 'flex-end' })
    },
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 12px',
      borderRadius: '4px',
      fontSize: '0.875rem',
      fontWeight: 500,
      border: '1px solid #ddd',
      backgroundColor: 'transparent',
      color: '#555',
      cursor: 'pointer',
      transition: 'background-color 0.2s, color 0.2s',
      '&:hover': {
        backgroundColor: '#f5f5f5'
      },
      ...(variant === 'compact' && {
        padding: '4px 8px',
        fontSize: '0.75rem'
      }),
      ...(variant === 'minimal' && {
        padding: '2px 6px',
        fontSize: '0.7rem',
        border: 'none'
      }),
      ...(variant === 'detailed' && {
        padding: '8px 16px',
        fontSize: '0.9rem'
      })
    },
    buttonIcon: {
      marginRight: '6px',
      fontSize: '1rem',
      ...(variant === 'compact' && { fontSize: '0.875rem' }),
      ...(variant === 'minimal' && { fontSize: '0.75rem', marginRight: '4px' })
    },
    dangerButton: {
      color: '#d32f2f',
      borderColor: '#ffcdd2',
      '&:hover': {
        backgroundColor: '#ffebee'
      }
    }
  };

  const actionsArray = [
    ...actions,
    onDelete && {
      label: 'Excluir',
      onClick: (e) => {
        e.stopPropagation();
        onDelete();
      },
      icon: '🗑️',
      variant: 'danger'
    }
  ].filter(Boolean);

  // Se não houver ações, não renderiza
  if (!actionsArray.length) return null;

  return (
    <div 
      className={`article-actions article-actions--${variant} ${className}`}
      style={styles.container}
      {...props}
    >
      {actionsArray.map((action, index) => {
        const buttonStyle = {
          ...styles.button,
          ...(action.variant === 'danger' ? styles.dangerButton : {})
        };

        return (
          <button
            key={action.label || index}
            className={`article-actions__button ${action.className || ''}`}
            style={buttonStyle}
            onClick={action.onClick}
            title={action.label}
            disabled={action.disabled}
            type="button"
          >
            {action.icon && (
              <span className="article-actions__button-icon" style={styles.buttonIcon}>
                {action.icon}
              </span>
            )}
            {action.label}
          </button>
        );
      })}
    </div>
  );
};

ArticleActions.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.node,
    variant: PropTypes.oneOf(['default', 'danger', 'primary']),
    disabled: PropTypes.bool,
    className: PropTypes.string
  })),
  variant: PropTypes.oneOf(['default', 'compact', 'minimal', 'detailed', 'list', 'chiclet']),
  className: PropTypes.string,
  onDelete: PropTypes.func
};

export default ArticleActions;